import { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  isCustomRangeValid,
  PeriodChoice,
  PRESETS,
  PresetId,
} from "./sales-by-period";

interface Props {
  choice: PeriodChoice;
  onChange: (choice: PeriodChoice) => void;
}

/**
 * One button per period; clicking a ready-made one applies it at once.
 * "Pasirinkti…" opens two date fields and applies only on "Rodyti", so the
 * table is not reloaded for every half-typed date.
 */
export default function PeriodButtons({ choice, onChange }: Props) {
  const { t } = useTranslation();
  const [isCustomOpen, setIsCustomOpen] = useState(choice.preset === "custom");
  const [draftFrom, setDraftFrom] = useState(choice.customFrom);
  const [draftTo, setDraftTo] = useState(choice.customTo);

  const pick = (preset: PresetId) => {
    if (preset === "custom") {
      setIsCustomOpen(true);
      return;
    }
    setIsCustomOpen(false);
    onChange({ ...choice, preset });
  };

  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label={t("customer_products_period")} className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => {
          const isActive = isCustomOpen ? preset.id === "custom" : preset.id === choice.preset;
          return (
            <button
              key={preset.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => pick(preset.id)}
              className={clsx(
                "rounded-full px-3 py-1 text-sm transition-colors max-md:py-3",
                isActive ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              {t(preset.labelKey)}
            </button>
          );
        })}
      </div>

      {isCustomOpen && (
        <div className="flex flex-wrap items-end gap-3">
          <DayField label={t("customer_products_custom_from")} value={draftFrom} onChange={setDraftFrom} />
          <DayField label={t("customer_products_custom_to")} value={draftTo} onChange={setDraftTo} />
          <button
            type="button"
            disabled={!isCustomRangeValid(draftFrom, draftTo)}
            onClick={() => onChange({ preset: "custom", customFrom: draftFrom, customTo: draftTo })}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 max-md:min-h-11"
          >
            {t("customer_products_custom_apply")}
          </button>
        </div>
      )}
    </div>
  );
}

function DayField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (day: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-600">
      {label}
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 md:text-sm"
      />
    </label>
  );
}
